import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, Users, Trophy, ChevronDown, ChevronUp } from 'lucide-react'

interface GameParticipant {
  id: string
  user_id: string
  team: 'red' | 'blue'
  role: 'defense' | 'attack' | 'full'
  user: {
    id: string
    display_name: string
  }
}

interface RecentGame {
  id: string
  date: string
  game_type: '1v1' | '2v2'
  target_score: number
  final_red_score: number
  final_blue_score: number
  winner?: 'red' | 'blue'
  quick_entry: boolean
  participants: GameParticipant[]
}

interface RecentGamesProps {
  games: RecentGame[]
  title?: string
  maxItems?: number
}

export function RecentGames({ games, title = "Recent Games", maxItems = 10 }: RecentGamesProps) {
  const [expanded, setExpanded] = useState(false)
  const defaultItemCount = 5
  const displayGames = expanded ? games.slice(0, maxItems) : games.slice(0, defaultItemCount)
  const hasMoreGames = games.length > defaultItemCount

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getGameTypeLabel = (gameType: string) => {
    return gameType === '1v1' ? '1v1' : '2v2'
  }

  const getWinner = (game: RecentGame) => {
    if (game.winner) {
      return game.winner
    }
    return 'tie'
  }

  const getParticipantsByTeam = (participants: GameParticipant[], team: 'red' | 'blue') => {
    return participants
      .filter(p => p.team === team)
      .map(p => p.user.display_name)
      .join(', ')
  }

  if (displayGames.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2" />
          {title}
        </h3>
        <div className="text-center text-gray-500 py-8">
          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No games recorded yet</p>
          <p className="text-sm">Start recording games to see them here!</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <Clock className="h-5 w-5 mr-2" />
        {title}
      </h3>
      <div className="space-y-3">
        {displayGames.map((game) => {
          const winner = getWinner(game)
          const redParticipants = getParticipantsByTeam(game.participants, 'red')
          const blueParticipants = getParticipantsByTeam(game.participants, 'blue')

          return (
            <Link
              key={game.id}
              to={`/games/${game.id}`}
              className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-600">
                    {getGameTypeLabel(game.game_type)}
                  </span>
                  <span className="text-xs text-gray-500">•</span>
                  <span className="text-sm text-gray-500">
                    {formatDate(game.date)}
                  </span>
                  {game.quick_entry && (
                    <>
                      <span className="text-xs text-gray-500">•</span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        Quick Entry
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  {winner !== 'tie' && (
                    <Trophy className="h-4 w-4 text-yellow-500" />
                  )}
                  <span className="text-sm font-medium text-gray-900">
                    {game.final_red_score} - {game.final_blue_score}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className={`p-2 rounded ${winner === 'red' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <div className="text-sm font-medium text-red-600 mb-1">Red Team</div>
                  <div className="text-xs text-gray-600">{redParticipants || 'No players'}</div>
                </div>
                <div className={`p-2 rounded ${winner === 'blue' ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                  <div className="text-sm font-medium text-blue-600 mb-1">Blue Team</div>
                  <div className="text-xs text-gray-600">{blueParticipants || 'No players'}</div>
                </div>
              </div>
            </Link>
          )
        })}
      </div>
      {hasMoreGames && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-4 py-2 px-4 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Show More ({games.length - defaultItemCount} more)
            </>
          )}
        </button>
      )}
    </div>
  )
}
